import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'semantic-ui-react';
import api from '../../../../../api';
import './SmsModals.css';

const A2PCampaignModal = ({ userId, campaignData, brandData, isEditMode, isApproved = false, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);
  const modalContentRef = useRef(null);
  const [formData, setFormData] = useState({
    campaign_name: '',
    description: '',
    message_flow: '',
    use_case: '',
    consent_type: '',
    help_message: 'Reply HELP for assistance',
    stop_message: 'Reply STOP to unsubscribe',
    sample_messages: ['', ''],
    terms_of_service_url: '',
    privacy_policy_url: '',
    has_embedded_links: false,
    has_embedded_phone: false,
  });
  const [errors, setErrors] = useState({});

  // Use case options matching the existing implementation
  const useCaseOptions = [
    { key: '2fa', text: 'Two-Factor Authentication (2FA)', value: '2FA' },
    { key: 'account_notification', text: 'Account Notifications', value: 'ACCOUNT_NOTIFICATION' },
    { key: 'customer_care', text: 'Customer Care & Support', value: 'CUSTOMER_CARE' },
    { key: 'delivery_notification', text: 'Delivery Notifications', value: 'DELIVERY_NOTIFICATION' },
    { key: 'fraud_alert', text: 'Fraud Alert Messaging', value: 'FRAUD_ALERT' },
    { key: 'higher_education', text: 'Higher Education', value: 'HIGHER_EDUCATION' },
    { key: 'low_volume', text: 'Low Volume Mixed', value: 'LOW_VOLUME' },
    { key: 'marketing', text: 'Marketing', value: 'MARKETING' },
    { key: 'mixed', text: 'Mixed Use Case', value: 'MIXED' },
    { key: 'polling_voting', text: 'Polling and Voting', value: 'POLLING_VOTING' },
    { key: 'public_service_announcement', text: 'Public Service Announcements', value: 'PUBLIC_SERVICE_ANNOUNCEMENT' },
    { key: 'security_alert', text: 'Security Alerts', value: 'SECURITY_ALERT' },
  ];

  // Consent type options
  const consentTypeOptions = [
    { key: 'web_form_with_opt_in', text: 'Web form with opt-in', value: 'web_form_with_opt_in' },
    { key: 'sms_opt_in', text: 'SMS opt-in', value: 'sms_opt_in' },
    { key: 'prior_customer_relationship', text: 'Prior customer relationship', value: 'prior_customer_relationship' },
    { key: 'written_agreement', text: 'Written agreement', value: 'written_agreement' },
    { key: 'inquired_via_inbound_call', text: 'Inquired via inbound call', value: 'inquired_via_inbound_call' },
    { key: 'other', text: 'Other', value: 'other' }
  ];

  useEffect(() => {
    if (isEditMode && campaignData) {
      setFormData({
        campaign_name: campaignData.campaign_name || '',
        description: campaignData.description || '',
        message_flow: campaignData.message_flow || '',
        use_case: campaignData.use_case || '',
        consent_type: campaignData.consent_type || '',
        help_message: campaignData.help_message || 'Reply HELP for assistance',
        stop_message: campaignData.stop_message || 'Reply STOP to unsubscribe',
        sample_messages: campaignData.sample_messages || ['', ''],
        terms_of_service_url: campaignData.terms_of_service_url || '',
        privacy_policy_url: campaignData.privacy_policy_url || '',
        has_embedded_links: campaignData.has_embedded_links === true || campaignData.has_embedded_links === 1,
        has_embedded_phone: campaignData.has_embedded_phone === true || campaignData.has_embedded_phone === 1,
      });
    }
  }, [isEditMode, campaignData]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Scroll to top when error appears
  useEffect(() => {
    if (error && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [error]);

  const handleSampleMessageChange = (index, value) => {
    const newMessages = [...formData.sample_messages];
    newMessages[index] = value;
    setFormData(prev => ({ ...prev, sample_messages: newMessages }));
    if (errors.sample_messages) {
      setErrors(prev => ({ ...prev, sample_messages: '' }));
    }
    if (error) setError('');
  };

  const addSampleMessage = () => {
    if (formData.sample_messages.length < 5) {
      setFormData(prev => ({
        ...prev,
        sample_messages: [...prev.sample_messages, '']
      }));
    }
  };

  const removeSampleMessage = (index) => {
    if (formData.sample_messages.length > 2) {
      const newMessages = formData.sample_messages.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, sample_messages: newMessages }));
    }
  };

  const validate = () => {
    const e = {};

    if (!formData.campaign_name?.trim()) {
      e.campaign_name = 'Campaign name is required';
    } else if (formData.campaign_name.length > 255) {
      e.campaign_name = 'Campaign name must be 255 characters or less';
    }

    if (!formData.description?.trim()) {
      e.description = 'Description is required';
    } else if (formData.description.length < 40) {
      e.description = 'Description must be at least 40 characters';
    } else if (formData.description.length > 4096) {
      e.description = 'Description cannot exceed 4096 characters';
    }

    if (!formData.message_flow?.trim()) {
      e.message_flow = 'Message flow is required';
    } else if (formData.message_flow.length < 40) {
      e.message_flow = 'Message flow must be at least 40 characters';
    } else if (formData.message_flow.length > 2048) {
      e.message_flow = 'Message flow cannot exceed 2048 characters';
    }

    if (!formData.use_case) {
      e.use_case = 'Use case is required';
    }

    if (!formData.consent_type) {
      e.consent_type = 'Consent type is required';
    }

    if (!formData.help_message?.trim()) {
      e.help_message = 'Help message is required';
    } else if (formData.help_message.length > 160) {
      e.help_message = 'Help message cannot exceed 160 characters';
    }

    if (!formData.stop_message?.trim()) {
      e.stop_message = 'Stop message is required';
    } else if (formData.stop_message.length > 160) {
      e.stop_message = 'Stop message cannot exceed 160 characters';
    }

    const validSamples = formData.sample_messages.filter(msg => msg.trim());
    if (validSamples.length < 2) {
      e.sample_messages = 'At least two sample messages are required';
    } else {
      const tooLong = validSamples.find(msg => msg.length > 160);
      if (tooLong) {
        e.sample_messages = 'Each sample message must be 160 characters or less';
      }
    }

    if (!formData.privacy_policy_url?.trim()) {
      e.privacy_policy_url = 'Privacy Policy URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.privacy_policy_url)) {
      e.privacy_policy_url = 'Please enter a valid URL (must start with http:// or https://)';
    }

    if (!formData.terms_of_service_url?.trim()) {
      e.terms_of_service_url = 'Terms of Service URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.terms_of_service_url)) {
      e.terms_of_service_url = 'Please enter a valid URL (must start with http:// or https://)';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      setError('Please fill in all required fields before proceeding.');
      // Scroll to top to show validation errors
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        sample_messages: formData.sample_messages.filter(msg => msg.trim()),
        user_id: parseInt(userId),
        brand_id: brandData?.id,
      };

      // Remove brand_id if not available
      if (!payload.brand_id) {
        delete payload.brand_id;
      }

      let response;
      if (isEditMode && campaignData?.id) {
        payload.id = campaignData.id;
        response = await api.outbound.updateA2PCampaign(payload);
      } else {
        response = await api.outbound.createA2PCampaign(payload);
      }

      if (response?.status || response?.success) {
        onClose(true);
      } else {
        if (response?.errors && typeof response.errors === 'object') {
          const errorMessages = Object.entries(response.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const msgList = Array.isArray(messages) ? messages : [messages];
              return `${fieldName}: ${msgList.join(', ')}`;
            })
            .join('\n');
          setError(errorMessages || response?.message || 'Validation failed.');
        } else {
          setError(response?.message || (isEditMode ? 'Failed to update campaign' : 'Failed to create campaign'));
        }
        // Scroll to top to show error message
        setTimeout(() => {
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = 0;
          }
        }, 100);
      }
    } catch (err) {
      console.error('A2P Campaign Submit Error:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);

      // Extract error message from API response
      const errorMessage = err.response?.data?.message
        || err.response?.data?.error
        || err?.message
        || 'Something went wrong while saving campaign. Please try again.';

      setError(errorMessage);
      // Scroll to top to show error message
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  return (
    <div className="sms-modal-overlay">
      <div className="sms-modal-content sms-modal-large">
        <div className="sms-modal-header">
          <h3>{isEditMode ? 'Edit A2P Campaign' : 'Create A2P Campaign'}</h3>
          <button
            className="sms-modal-close"
            onClick={() => onClose(false)}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="sms-modal-body" ref={modalContentRef}>
            {error && (
              <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }} ref={errorRef}>
                {error}
              </div>
            )}

         

            {/* Campaign Information */}
            <div className="form-section">
              <h4>Campaign Information</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Campaign Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="campaign_name"
                    className={`form-input ${errors.campaign_name ? 'error' : ''}`}
                    value={formData.campaign_name}
                    onChange={handleInputChange}
                    placeholder="Enter your campaign name"
                    disabled={loading || isApproved}
                  />
                  {errors.campaign_name && (
                    <span className="error-message">{errors.campaign_name}</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Campaign Description <span className="required">*</span></label>
                  <textarea
                    name="description"
                    className={`form-input ${errors.description ? 'error' : ''}`}
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe the purpose of this campaign (minimum 40 characters)"
                    rows="4"
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">Minimum 40 characters</span>
                  {errors.description && (
                    <span className="error-message">{errors.description}</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Message Flow <span className="required">*</span></label>
                  <textarea
                    name="message_flow"
                    className={`form-input ${errors.message_flow ? 'error' : ''}`}
                    value={formData.message_flow}
                    onChange={handleInputChange}
                    placeholder="Describe how customers will interact with your messages (minimum 40 characters)"
                    rows="3"
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">Minimum 40 characters</span>
                  {errors.message_flow && (
                    <span className="error-message">{errors.message_flow}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Use Case <span className="required">*</span></label>
                  <Dropdown
                    placeholder="Select Use Case"
                    fluid
                    selection
                    options={useCaseOptions}
                    value={formData.use_case}
                    onChange={(e, { value }) => {
                      setFormData(prev => ({ ...prev, use_case: value }));
                      setErrors(prev => ({ ...prev, use_case: '' }));
                      setError('');
                    }}
                    disabled={loading || isApproved}
                    className={errors.use_case ? 'error' : ''}
                  />
                  {errors.use_case && (
                    <span className="error-message">{errors.use_case}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Consent Type <span className="required">*</span></label>
                  <Dropdown
                    placeholder="Select Consent Type"
                    fluid
                    selection
                    options={consentTypeOptions}
                    value={formData.consent_type}
                    onChange={(e, { value }) => {
                      setFormData(prev => ({ ...prev, consent_type: value }));
                      setErrors(prev => ({ ...prev, consent_type: '' }));
                      setError('');
                    }}
                    disabled={loading || isApproved}
                    className={errors.consent_type ? 'error' : ''}
                  />
                  {errors.consent_type && (
                    <span className="error-message">{errors.consent_type}</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Message Content</label>
                  <div style={{ display: 'flex', gap: '32px', marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isApproved ? 'default' : 'pointer', fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={formData.has_embedded_links}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (!isApproved) {
                            setFormData(prev => ({ ...prev, has_embedded_links: checked }));
                          }
                        }}
                        disabled={loading || isApproved}
                      />
                      Messages contain embedded links (URLs)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isApproved ? 'default' : 'pointer', fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={formData.has_embedded_phone}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (!isApproved) {
                            setFormData(prev => ({ ...prev, has_embedded_phone: checked }));
                          }
                        }}
                        disabled={loading || isApproved}
                      />
                      Messages contain embedded phone numbers
                    </label>
                  </div>
                  <span className="field-hint">Check only if your actual messages include URLs or phone numbers. Incorrect declarations can cause TCR rejection.</span>
                </div>
              </div>
            </div>

            {/* Auto-Response Messages */}
            <div className="form-section">
              <h4>Auto-Response Messages</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Help Message <span className="required">*</span></label>
                  <textarea
                    name="help_message"
                    className={`form-input ${errors.help_message ? 'error' : ''}`}
                    value={formData.help_message}
                    onChange={handleInputChange}
                    rows="3"
                    maxLength={160}
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">Maximum 160 characters</span>
                  {errors.help_message && (
                    <span className="error-message">{errors.help_message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Stop Message <span className="required">*</span></label>
                  <textarea
                    name="stop_message"
                    className={`form-input ${errors.stop_message ? 'error' : ''}`}
                    value={formData.stop_message}
                    onChange={handleInputChange}
                    rows="3"
                    maxLength={160}
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">Maximum 160 characters</span>
                  {errors.stop_message && (
                    <span className="error-message">{errors.stop_message}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Website Audit Information */}
            <div className="form-section">
              <h4>Website Audit Information</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Privacy Policy URL <span className="required">*</span></label>
                  <input
                    type="url"
                    name="privacy_policy_url"
                    className={`form-input ${errors.privacy_policy_url ? 'error' : ''}`}
                    value={formData.privacy_policy_url}
                    onChange={handleInputChange}
                    placeholder="https://yourcompany.com/privacy"
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">
                    Your privacy policy URL that explains how you handle customer data and consent
                  </span>
                  {errors.privacy_policy_url && (
                    <span className="error-message">{errors.privacy_policy_url}</span>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Terms of Service URL <span className="required">*</span></label>
                  <input
                    type="url"
                    name="terms_of_service_url"
                    className={`form-input ${errors.terms_of_service_url ? 'error' : ''}`}
                    value={formData.terms_of_service_url}
                    onChange={handleInputChange}
                    placeholder="https://yourcompany.com/terms"
                    disabled={loading || isApproved}
                  />
                  <span className="field-hint">
                    Your terms of service URL that users agree to when opting in
                  </span>
                  {errors.terms_of_service_url && (
                    <span className="error-message">{errors.terms_of_service_url}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Sample Messages */}
            <div className="form-section">
              <h4>Sample Messages</h4>
              <div className="form-group">
                <label>Provide Sample Messages <span className="required">*</span></label>
                {formData.sample_messages.map((msg, index) => (
                  <div key={index} style={{ display: 'flex', marginBottom: '12px', alignItems: 'flex-start' }}>
                    <textarea
                      className="form-input"
                      rows="2"
                      placeholder={`Sample message ${index + 1} (max 160 characters)`}
                      value={msg}
                      onChange={(e) => handleSampleMessageChange(index, e.target.value)}
                      disabled={loading || isApproved}
                      maxLength={160}
                      style={{ flex: 1 }}
                    />
                    {formData.sample_messages.length > 2 && !loading && (
                      <button
                        type="button"
                        onClick={() => removeSampleMessage(index)}
                        style={{
                          minWidth: '40px',
                          width: '40px',
                          height: '40px',
                          marginLeft: '8px',
                          padding: '0',
                          border: '1px solid #ef4444',
                          borderRadius: '8px',
                          backgroundColor: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#ef4444';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {formData.sample_messages.length < 5 && !loading && (
                  <button
                    type="button"
                    className="btn linear-gradient zy-save-btn ml-2"
                    onClick={addSampleMessage}
                    style={{ marginTop: '8px' }}
                  >
                    + Add Sample Message
                  </button>
                )}
                <span className="field-hint" style={{ display: 'block', marginTop: '8px' }}>
                  At least two sample messages required. Maximum 160 characters each.
                </span>
                {errors.sample_messages && (
                  <span className="error-message">{errors.sample_messages}</span>
                )}
              </div>
            </div>

            
          </div>

          <div className="sms-modal-footer">
            <button
              type="button"
              className="btn linear-gradient-cancel yr-cancelbtn mg-r"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              {isApproved ? 'Close' : 'Cancel'}
            </button>
            {!isApproved && (
              <button
                type="submit"
                className="btn linear-gradient zy-save-btn ml-2"
                disabled={loading}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }} role="status" aria-hidden="true"></span>
                )}
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Campaign' : 'Create Campaign')}
              </button>
            )}
            {isApproved && (
              <div className="approval-notice" style={{
                color: '#059669',
                fontWeight: 600,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>✅</span>
                Campaign is approved and cannot be edited
              </div>
            )}
          </div>
        </form>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '14px', fontWeight: 500 }}>
                {isEditMode ? 'Updating A2P Campaign...' : 'Creating A2P Campaign...'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default A2PCampaignModal;
